/**
 * Dependency-free string fuzzy-matching helpers, shared by repertoire genre
 * deduping (see `repertoireFilters.ts`) and repertoire search (see `Browse.tsx`).
 *
 * A `fast-levenshtein` package exists in node_modules but only as a transitive
 * dependency, so it isn't safe to import directly — hence the hand-rolled DP.
 */

/** Levenshtein edit distance between two strings (space-efficient two-row DP). */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prevRow = Array.from({ length: b.length + 1 }, (_unused, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const currRow = [i];
    for (let j = 1; j <= b.length; j++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      currRow[j] = Math.min(
        prevRow[j] + 1, // deletion
        currRow[j - 1] + 1, // insertion
        prevRow[j - 1] + substitutionCost, // substitution
      );
    }
    prevRow = currRow;
  }
  return prevRow[b.length];
}

/**
 * Minimum edit distance between `query` and the best-matching substring of
 * `text` — i.e. how many single-character typos separate the query from
 * appearing *somewhere inside* the text. A query that occurs verbatim scores 0.
 *
 * This is the standard Levenshtein DP with the first row pinned to zero (so a
 * match may begin at any position in `text`) and the answer taken as the best
 * end position (so it may end anywhere). Used for typo-tolerant "does this query
 * occur in this field" search, where the query is usually much shorter than the
 * field and a whole-string edit distance would be meaningless.
 */
export function fuzzySubstringDistance(query: string, text: string): number {
  if (query.length === 0) return 0;
  if (text.length === 0) return query.length;
  // Row 0 = all zeros: matching an empty query prefix costs nothing wherever it
  // starts in `text`.
  let prevRow = new Array<number>(text.length + 1).fill(0);
  for (let i = 1; i <= query.length; i++) {
    const currRow = [i]; // matching against an empty text prefix needs i deletions
    for (let j = 1; j <= text.length; j++) {
      const substitutionCost = query[i - 1] === text[j - 1] ? 0 : 1;
      currRow[j] = Math.min(
        prevRow[j] + 1, // deletion (drop a query char)
        currRow[j - 1] + 1, // insertion (skip a text char inside the window)
        prevRow[j - 1] + substitutionCost, // substitution
      );
    }
    prevRow = currRow;
  }
  // Best matching window can end at any position, so take the row minimum.
  return Math.min(...prevRow);
}

/**
 * Character budget for treating two pieces of text as "the same" up to typos,
 * scaled to length: exact-only for short strings (where a one-character change
 * usually means a genuinely different word — e.g. "Folk" vs "Funk", or a "bach"
 * query drifting into "beach"), then 1 character for medium and 2 for long
 * strings, where typos are both more likely and less ambiguous.
 */
export function typoBudget(length: number): number {
  if (length < 5) return 0;
  if (length < 9) return 1;
  return 2;
}

/**
 * More lenient character budget for repertoire *search* (as opposed to the
 * strict {@link typoBudget} used for genre deduping, where a wrong merge hides a
 * whole category). Roughly one tolerated typo per three characters, capped at
 * three, but still exact-only for very short queries where fuzziness would match
 * almost anything. Search errs toward showing a few extra results over hiding
 * the one the user meant.
 */
export function searchTypoBudget(queryLength: number): number {
  if (queryLength < 4) return 0;
  return Math.min(3, Math.floor(queryLength / 3));
}

/**
 * True when `query` occurs within `field` allowing for a small number of typos.
 * Both inputs are expected to be pre-normalized (lowercased, accents/punctuation
 * stripped) by the caller. Exact substrings always match; beyond that the query
 * length sets the typo tolerance via {@link searchTypoBudget}, judged as a fuzzy
 * substring so it still matches when the query is only part of the field.
 */
export function fuzzyFieldMatch(field: string, query: string): boolean {
  if (query === '') return true;
  if (field.includes(query)) return true;
  const budget = searchTypoBudget(query.length);
  if (budget === 0) return false;
  return fuzzySubstringDistance(query, field) <= budget;
}
